/* useWishlist — localStorage-backed list of product slugs.
 *
 * Phase 2 keeps wishlists client-only; Phase 3 will replace this with a
 * server-backed endpoint behind the same interface so call sites don't
 * change.
 *
 *   const { slugs, has, toggle, clear, count } = useWishlist();
 *   <button onClick={() => toggle("amoxicillin-500mg")} />
 */
import React from "react";

const KEY = "nethrasap.wishlist";
const EVENT = "nethrasap:wishlist-changed";

function read() {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((s) => typeof s === "string") : [];
  } catch (_) {
    return [];
  }
}

function write(slugs) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(slugs));
  } catch (_) {}
  // Notify other components in the same tab.
  window.dispatchEvent(new CustomEvent(EVENT, { detail: slugs }));
}

export function useWishlist() {
  const [slugs, setSlugs] = React.useState(read);

  React.useEffect(() => {
    const onChange = (e) => {
      const next = e?.detail ?? read();
      setSlugs(next);
    };
    // Same-tab updates
    window.addEventListener(EVENT, onChange);
    // Cross-tab updates
    const onStorage = (e) => {
      if (e.key === KEY) setSlugs(read());
    };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVENT, onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const has = React.useCallback((slug) => slugs.includes(slug), [slugs]);

  const toggle = React.useCallback((slug) => {
    if (!slug) return;
    const next = slugs.includes(slug)
      ? slugs.filter((s) => s !== slug)
      : [...slugs, slug];
    write(next);
    setSlugs(next);
  }, [slugs]);

  const clear = React.useCallback(() => {
    write([]);
    setSlugs([]);
  }, []);

  return { slugs, count: slugs.length, has, toggle, clear };
}
